import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import {
	MsdynamicsCompanySyncTriggerResponse,
	MsdynamicsConversationSyncResponse,
	MsdynamicsDealConfigResponse,
	MsdynamicsDealCreationTriggerResponse,
	MsdynamicsLeadConfigResponse,
	MsdynamicsListSyncTriggerResponse,
	MsdynamicsOAuthStatusResponse,
} from "../../pages/Company/schema/dynamics";

import { dynamicsApi } from "./dynamics.api";

interface DynamicsState {
	oauthStatus: MsdynamicsOAuthStatusResponse | null;
	conversationSync: MsdynamicsConversationSyncResponse | null;
	companySyncTrigger: MsdynamicsCompanySyncTriggerResponse | null;
	dealCreationTrigger: MsdynamicsDealCreationTriggerResponse | null;
	dealConfig: MsdynamicsDealConfigResponse | null;
	leadConfig: MsdynamicsLeadConfigResponse | null;
	/**
	 * The per-list sync trigger is the one mirrored query whose **arg** selects the data,
	 * so it gets one bucket per `listId` (web §2). A single "latest" bucket would go stale
	 * the moment the user returns to a list whose entry is still warm: RTK Query serves the
	 * cache without re-dispatching `fulfilled`, so the reducer never runs again.
	 */
	listSyncTriggerByListId: Record<number, MsdynamicsListSyncTriggerResponse>;
}

const initialState: DynamicsState = {
	oauthStatus: null,
	conversationSync: null,
	companySyncTrigger: null,
	dealCreationTrigger: null,
	dealConfig: null,
	leadConfig: null,
	listSyncTriggerByListId: {},
};

export const dynamicsSlice = createSlice({
	name: "dynamics",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsOAuthStatus.matchFulfilled,
			(state, { payload }) => {
				state.oauthStatus = payload;
			}
		);
		builder.addMatcher(dynamicsApi.endpoints.disconnectDynamicsOAuth.matchFulfilled, (state) => {
			state.oauthStatus = { connected: false };
		});
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsConversationSync.matchFulfilled,
			(state, { payload }) => {
				state.conversationSync = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.updateDynamicsConversationSync.matchFulfilled,
			(state, { payload }) => {
				state.conversationSync = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsCompanySyncTrigger.matchFulfilled,
			(state, { payload }) => {
				state.companySyncTrigger = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.updateDynamicsCompanySyncTrigger.matchFulfilled,
			(state, { payload }) => {
				state.companySyncTrigger = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsDealCreationTrigger.matchFulfilled,
			(state, { payload }) => {
				state.dealCreationTrigger = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.updateDynamicsDealCreationTrigger.matchFulfilled,
			(state, { payload }) => {
				state.dealCreationTrigger = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsDealConfig.matchFulfilled,
			(state, { payload }) => {
				state.dealConfig = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.saveDynamicsDealConfig.matchFulfilled,
			(state, { payload }) => {
				state.dealConfig = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsLeadConfig.matchFulfilled,
			(state, { payload }) => {
				state.leadConfig = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.saveDynamicsLeadConfig.matchFulfilled,
			(state, { payload }) => {
				state.leadConfig = payload;
			}
		);
		// Filed under the listId the response was **requested** for, never the currently
		// selected one — a response can land after the user has already switched lists.
		builder.addMatcher(
			dynamicsApi.endpoints.getDynamicsListSyncTrigger.matchFulfilled,
			(state, { payload, meta }) => {
				state.listSyncTriggerByListId[meta.arg.originalArgs] = payload;
			}
		);
		builder.addMatcher(
			dynamicsApi.endpoints.updateDynamicsListSyncTrigger.matchFulfilled,
			(state, { payload, meta }) => {
				state.listSyncTriggerByListId[meta.arg.originalArgs.listId] = payload;
			}
		);
	},
});

export const dynamicsReducer = dynamicsSlice.reducer;

export const dynamicsSelector = (state: RootState) => state[dynamicsSlice.name];

export const dynamicsListSyncTriggerSelector =
	(listId: number) =>
	(state: RootState): MsdynamicsListSyncTriggerResponse | undefined =>
		state[dynamicsSlice.name].listSyncTriggerByListId[listId];
